/**
  * @class mmodOfficeImport
  * @description
  * Import module implements importing of data from .mm files.
  * It provides unified user interface leveraging multiple server services
  * to import data of various types.
  * It can be invoked from other Office modules which can specify
  * allowed and pre-selected types for import.
  */

import mmodModule from 'mmodModule'
const ms = {
  ...require('msysMessage/binary'),
  ...require('msysMessage/file'),
}
const mcrypt = require('msysCrypt')

export default class mmodOfficeImport extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'float', x: 0, y: 0, width: '100%', height: '100%', order: 1 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      mapping: {
        type: 'object',
        patternProperties: { '^mm[A-Z]\\w+$': { type: 'string', minLength: 1 } },
        additionalProperties: false,
        default: {
          mmDopplerHit: ':msvrWfDopplers',
          mmDopplerCalibration: ':msvrWfDopplerCalibrations',
          mmDelict: ':msvrDelicts',
        },
        description: 'Mapping of action types to server services for import; keys are action names (mm*), values are server service addresses',
      },
    }, cfg_schema)

    const input = document.createElement('input')
    if(!('webkitdirectory' in input)) {
      document.body.classList.add('nowebkitdirectory')
    }

    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    const dropzone = this.el.appendChildren({ tag: 'mele-text', class: 'dropzone', label: 'DropFiles' })

    // dragenter/dragleave must be on root element
    // as dropzone has by default pointer-events: none to allow working on the elements below
    const root = document.body
    root.addEventListener('dragenter', (e) => {
      if(e.dataTransfer.types.includes('Files')) {
        // handle only drag&drop started from outside of browser
        dropzone.classList.add('dragover')
      }
    })
    root.addEventListener('dragleave', e => {
      if(e.target === root || e.relatedTarget == null) {
        dropzone.classList.remove('dragover')
      }
    })
    root.addEventListener('drop', () => {
      dropzone.classList.remove('dragover')
    })

    // dragover and drop must be on the dropzone
    dropzone.ondragover = e => {
      if(e.dataTransfer.types.includes('Files')) {
        // handle only drag&drop started from outside of browser
        e.preventDefault()
      }
    }
    dropzone.ondrop = e => {
      if(e.dataTransfer.types.includes('Files')) {
        // handle only drag&drop started from outside of browser
        e.preventDefault()
        dropzone.classList.remove('dragover')
        const sender = {
          listener_id: this.focused_window,
          scope: '',
        }
        const params = {
        }
        this.importFiles(sender, params, e.dataTransfer.files)
      }
    }

    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  async mmWindowFocused(sender, params) {
    if(params.id) {
      this.focused_window = params.id
    }
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * request to open file selection dialog for import
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {boolean} [params.folders] - if true, import folders, otherwise files
   * @param {array} [params.types] - list of user selectable types for import
   * @param {array} [params.selected] - list of pre-selected types for import
   */
  mmImportPrompt(sender, params) {
    const input = document.createElement('input')
    input.style.display = 'none'
    input.type = 'file'
    input.multiple = true
    if(params.folders) {
      input.webkitdirectory = true
      input.directory = true
    }

    input.onchange = async () => {
      this.importFiles(sender, params, input.files)
    }

    document.body.appendChild(input)
    input.click()
    document.body.removeChild(input)
  } // mmImportPrompt

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Imports data from selected files.
  * @param {object} module - requesting GUI module object
  * @param {object} files - files to be imported
  */
  async importFiles(sender, params, files) {
    function readFile(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => resolve()
        reader.readAsArrayBuffer(file)
      })
    }

    // function to filter only blocks with configured action types
    const mapping = this.config.mapping || {}
    const action_id_map = {}
    for(const action in mapping) {
      const aid = String(ms.action2id(action) ?? '')
      if(aid) action_id_map[aid] = mapping[action]
    }
    const filter = async (block) => {
      const [, action_id] = ms.uint2dtactionid(block)
      return action_id in action_id_map
    }

    const import_id = 'office_'+mcrypt.oid()

    const elModal = this.modalOpen({
      key: 'mmodOfficeImport',
      header: 'Import',
      position: 'center',
      items: [
        // eslint-disable-next-line no-template-curly-in-string
        { tag: 'mele-input', type: 'string', readonly: true, itemlabel: '${key}', key: 'out_dir', label: 'import.ImportSource', localize: true, value: 'import.LocalFilesystem' },
        { tag: 'mele-input', key: 'records_good', label: 'import.Good', readonly: true, value: '0', width: '1/2' },
        { tag: 'mele-input', key: 'records_bad', label: 'import.Bad', readonly: true, value: '0', width: '1/2' },
        { tag: 'mele-input', key: 'progress', label: 'import.Progress', readonly: true, value: '0/0', style: '--progress: 0%;' },
        { tag: 'div', key: 'log' },
      ],
      footerbuttons: [
        { key: 'importStop', color: 'red', label: 'import.StopImport' },
      ],
    })

    elModal.import_ref = Date.now()

    let aborted = ''
    elModal.find('@importStop').eventButtonClicked = () => {
      aborted = '{{import.AbortedByUser}}'
    }

    let good_records = 0
    let bad_records = 0
    let good_files = 0
    let bad_files = 0

    const elRecordsGood = elModal.find('@records_good')
    const elRecordsBad = elModal.find('@records_bad')
    const elProgress = elModal.find('@progress')
    const elLog = elModal.find('@log')

    const _of = elModal.translate('import.of')
    const _files = elModal.translate('import.files')
    function fileProcessed(error) {
      if(error) {
        bad_files++
      }else {
        good_files++
      }
      const processed = good_files + bad_files
      elProgress.value = `${processed} ${_of} ${files.length} ${_files}`
      elProgress.style.setProperty('--progress', Math.round(processed / files.length * 100) + '%')
    }

    function log(msg) {
      msg = elModal.translate(msg)
      elLog.innerHTML += msg
      elLog.scrollTop = elLog.scrollHeight
    }

    log(`<info>{{import.Found}} ${files.length} {{import.files}}</info>`)

    for(const file of files) {
      if(aborted) break
      if(!file.name.endsWith('.mm')) continue

      log(`<header>{{import.ImportingFile}} ${file.name}</header>`)

      const buf = await readFile(file)
      if(aborted) break

      if(!buf) {
        fileProcessed(true)
        log(`<err>{{import.FileError}} ${file.name}</err>`)
        continue
      }

      const uint = new Uint8Array(buf)
      const blocks = await ms.readBufferBlocks(uint, filter)
      if(aborted) break

      let good = 0
      let bad = 0
      for(const binary of blocks) {
        const [event_dt, action_id] = ms.uint2dtactionid(binary)
        const server_service = action_id_map[action_id]
        if(!server_service) continue
        let error
        try {
          const { action, params: reply } = await this.request(server_service, 'mmAction', {
            action: 'IMPORT',
            params: { import_id, binary },
          })
          if(action!=='mmObjects') {
            error = String(reply.error || action || 'no action in response')
          }
        }catch(e) {
          error = String(e)
        }
        if(error) {
          bad++
          bad_records++
          elRecordsBad.value = bad_records
          if(/action denied: IMPORT/.test(error)) {
            log(`<warn>{{import.Record}} ${event_dt} {{import.NotImported}}: {{import.AlreadyExists}}</warn>`)
          }else if(error === 'server_disconnected') {
            log(`<err>{{import.Record}} ${event_dt} {{import.NotImported}}: {{import.ServerDisconnected}}</err>`)
            aborted = '{{import.ServerDisconnected}}'
            break
          }else{
            log(`<err>{{import.Record}} ${event_dt} {{import.NotImported}}: ${error}</err>`)
          }
        }else {
          good++
          good_records++
          elRecordsGood.value = good_records
          log(`<ok>{{import.Record}} ${event_dt} {{import.Imported}}</ok>`)
        }
        if(aborted) break
      }

      fileProcessed()
      log(`<sum>${good+bad} {{import.records_found}}: ${good} {{import.records_good}} ${bad} {{import.records_bad}}</sum>`)
    }

    log(`<info>${files.length} {{import.files_found}}: ${good_files} {{import.files_good}} ${bad_files} {{import.files_bad}}</info>`)
    log(`<info>${good_records+bad_records} {{import.records_found}}: ${good_records} {{import.records_good}} ${bad_records} {{import.records_bad}}</info>`)

    if(aborted) {
      log(`<abort>{{import.Interrupted}}: ${aborted}</abort>`)
    }else {
      log('<success>{{import.Finished}}</success>')
    }

    // after end change footer buttons
    elModal.footerbuttons = [
      { key: 'importDownloadLog', label: 'import.DownloadLog', action: 'submitalways', color: 'gray' },
      { key: 'close', color: 'blue', label: 'form.close', action: 'modalclose' },
    ]
  } // importFiles

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * download import log
  */
  async importDownloadLog() {
    const elImport = this.modalFind('mmodOfficeImport')
    if(!elImport) return
    let { out_dir, progress, records_good, records_bad } = elImport.getValues(true, true, true)
    if(elImport.find('@out_dir').localize) out_dir = this.el.translate(out_dir)

    const elLog = elImport.find('@log')
    if(!elLog) return

    const translate = (x) => {
      return this.el.translate('import.' + x)
    }

    const dt = new Date()
    const html = `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="UTF-8">
      <style>
      h1 {
        font-size: 2em;
        padding: 0;
        margin: 0;
      }
      h2 {
        font-size: 1.5em;
        padding: 0;
        margin: 0;
      }
      h2 + div {
        padding-top: 1em;
      }
      div {
        font-size: 0.8em;
      }
      info {
        display: block;
        font-weight: bold;
        font-size: 1.5em;
        color: blue;
      }
      abort {
        display: block;
        font-weight: bold;
        font-size: 1.5em;
        color: red;
      }
      success {
        display: block;
        font-weight: bold;
        font-size: 1.5em;
        color: green;
      }
      div header {
        display: block;
        padding-top: 1em;
      }
      ok {
        display: block;
        padding-left: 1em;
        color: green;
      }
      warn {
        display: block;
        padding-left: 1em;
        color: orange;
      }
      err {
        display: block;
        padding-left: 1em;
        color: red;
      }
      sum {
        display: block;
        padding-left: 1em;
        padding-bottom: 1em;
      }

      </style>
    </head>
    <body>
      <h1>${translate('report.header')}</h1>
      <h2>${translate('report.date')}: ${_.userdatetime(dt)}</h2>
      <h2>${translate('report.source')}: ${out_dir}</h2>
      <h2>${translate('report.progress')}: ${progress}</h2>
      <h2>${translate('Good')}: ${records_good}</h2>
      <h2>${translate('Bad')}: ${records_bad}</h2>
      <div>${elLog.innerHTML}</div>
    </body>
  </html>`

    const el = document.createElement('a')
    el.style.display = 'none'
    el.setAttribute('href', 'data:text/html;charset=utf-8,' + encodeURIComponent(html))
    el.setAttribute('download', `mosy_import_${_.ymdhms(dt)}.htm`)
    document.body.appendChild(el)
    el.click()
    document.body.removeChild(el)
  } // importDownloadLog

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sent by &lt;mele-container> or derived class to parent module when form is submitted
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} params.holder - key attribute of the form
   * @param {string} [params.button] - key attribute of the button
   * @param {string} [params.datakey] - key attribute of the content
   * @param {object} params.values - hash of key:value of contained editable inputs
   * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
   * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
   */
  mmFormSubmit(sender, params) {
    const { button } = params
    if(button === 'importDownloadLog') {
      this.importDownloadLog()
    }
  } // mmFormSubmit
} // mmodOfficeImport
